import { z } from 'zod';

/** Status eines Altdaten-Import-Jobs (ADR 0026). */
export const importJobStatusSchema = z.enum([
  'PENDING', // angelegt, wartet auf Upload
  'UPLOADING', // Datei wird hochgeladen
  'UPLOADED', // Datei vollständig, Lauf eingereiht
  'RUNNING', // ETL läuft
  'DONE', // erfolgreich
  'FAILED', // abgebrochen (siehe error)
  'CANCELLED',
]);
export type ImportJobStatus = z.infer<typeof importJobStatusSchema>;

/** Zeilenzahlen je Zieltabelle. */
const importCountsSchema = z.object({
  customers: z.number(),
  contacts: z.number(),
  activities: z.number(),
  deliveryAddresses: z.number(),
  articles: z.number(),
  salesDocuments: z.number(),
  purchaseOrders: z.number(),
  salesItems: z.number(),
  purchaseItems: z.number(),
});
export type ImportCountsDto = z.infer<typeof importCountsSchema>;

/**
 * Validierungsbericht: eingefügte Zeilen je Zieltabelle, plus optional die je
 * Tabelle **übersprungenen** Zeilen (fehlende Pflicht-Verknüpfung — z. B.
 * Belegposition ohne zugeordneten Beleg, Kontakt ohne Kunde). So bleibt ein
 * „0 importiert" nicht stumm, sondern erklärt sich.
 */
export const importReportSchema = importCountsSchema.extend({
  skipped: importCountsSchema.optional(),
});
export type ImportReportDto = z.infer<typeof importReportSchema>;

/**
 * Importierbare Entitäten (= NDJSON-Dateien) in FK-Lade-Reihenfolge.
 * Schlüssel entsprechen den Manifest-Einträgen.
 */
export const IMPORT_ENTITIES = [
  'customers',
  'articles',
  'contacts',
  'crm_activities',
  'delivery_addresses',
  'sales_documents',
  'sales_document_items',
  'purchase_orders',
  'purchase_order_items',
] as const;
export type ImportEntity = (typeof IMPORT_ENTITIES)[number];

export const IMPORT_ENTITY_LABELS: Record<ImportEntity, string> = {
  customers: 'Kunden',
  articles: 'Artikel',
  contacts: 'Kontakte',
  crm_activities: 'CRM-Aktivitäten',
  delivery_addresses: 'Lieferadressen',
  sales_documents: 'Belege',
  sales_document_items: 'Belegpositionen',
  purchase_orders: 'Bestellungen',
  purchase_order_items: 'Bestellpositionen',
};

/** Ein Ziel-(ProERP-)Feld je Entität — Schlüssel = kanonischer NDJSON-Name. */
export interface ImportFieldDef {
  key: string;
  label: string;
  /** Pflichtfeld (UUID/Fremdschlüssel) — ohne gültigen Wert wird der Satz übersprungen. */
  required?: boolean;
  /**
   * Quell-Spaltennamen (dt./engl. Schreibweisen), die beim Auto-Mapping auf
   * dieses Feld zeigen. Vergleich ist diakritik-/format-tolerant (siehe
   * `normalizeColumnName`). Der kanonische `key` wird immer zusätzlich geprüft.
   */
  aliases?: string[];
}

/** Mappbare Zielfelder je Entität (Basis der Mapping-UI). */
export const IMPORT_ENTITY_FIELDS: Record<ImportEntity, ImportFieldDef[]> = {
  customers: [
    { key: 'id', label: 'ID (uuid, leer = automatisch)' },
    { key: 'number', label: 'Kundennummer', required: true, aliases: ['nummer', 'kundennummer', 'kundennr', 'kunden_nr', 'debitor'] },
    { key: 'name', label: 'Name (komplett, bevorzugt)', aliases: ['name_komplett', 'name', 'firma'] },
    { key: 'name1', label: 'Name (Zeile 1)', aliases: ['name1'] },
    { key: 'name2', label: 'Name (Zeile 2)', aliases: ['name2'] },
    { key: 'name3', label: 'Name (Zeile 3)', aliases: ['name3'] },
    { key: 'matchcode', label: 'Matchcode/Kurzname', aliases: ['kurzname', 'matchcode', 'suchbegriff'] },
    { key: 'type', label: 'Typ (aus adressart: „Lieferant" → Lieferant, „…Interessent" → Interessent)', aliases: ['adressart', 'typ'] },
    { key: 'status', label: 'Status', aliases: ['status'] },
    { key: 'email', label: 'E-Mail', aliases: ['email', 'e_mail', 'mail'] },
    { key: 'phone', label: 'Telefon', aliases: ['telefon', 'tel', 'telefon1', 'phone'] },
    { key: 'fax', label: 'Telefax', aliases: ['telefax', 'fax'] },
    { key: 'website', label: 'Web', aliases: ['web', 'website', 'homepage', 'www'] },
    { key: 'vat_id', label: 'USt-IdNr.', aliases: ['ust_idnr', 'ustidnr', 'uid', 'vatid', 'umsatzsteuer'] },
    { key: 'street', label: 'Straße', aliases: ['strasse', 'str'] },
    { key: 'postal_code', label: 'PLZ', aliases: ['plz', 'postleitzahl'] },
    { key: 'city', label: 'Ort', aliases: ['ort', 'stadt'] },
    { key: 'country', label: 'Land (ISO-Code, z. B. DE)', aliases: ['land_code', 'laenderkennzeichen', 'iso'] },
    { key: 'country_name', label: 'Land (Klartext)', aliases: ['land', 'landname'] },
    { key: 'warning_text', label: 'Adresswarntext', aliases: ['warntext', 'warnung', 'adresswarntext'] },
    { key: 'payment_terms_days', label: 'Zahlungsziel (Tage)', aliases: ['netto_tage', 'zahlungsziel', 'zahlungsziel_tage'] },
    { key: 'language', label: 'Sprache', aliases: ['sprache', 'language'] },
    { key: 'tax_region_code', label: 'Steuergebiet (landesart: 0/1/2)', aliases: ['landesart', 'steuergebiet'] },
    { key: 'address_type_code', label: 'Adressart-Code (20/21/22)', aliases: ['adressart_code', 'adressartcode'] },
    { key: 'notes', label: 'Notizen', aliases: ['notizen', 'notiz', 'bemerkung', 'anmerkung'] },
    { key: 'inactive_flag', label: 'Inaktiv (J/N → Status)', aliases: ['inaktiv', 'inactive'] },
    { key: 'billing_blocked', label: 'Belegsperre (J/N)', aliases: ['belegsperre', 'sperre'] },
  ],
  articles: [
    { key: 'id', label: 'ID (uuid, leer = automatisch)' },
    { key: 'number', label: 'Artikelnummer', required: true, aliases: ['artikelnummer', 'artikelnr', 'artnr', 'nummer'] },
    { key: 'name', label: 'Bezeichnung (DE)', aliases: ['bezeichnung_de', 'bezeichnung', 'artikelbezeichnung', 'name'] },
    { key: 'name_en', label: 'Bezeichnung (EN)', aliases: ['bezeichnung_en', 'bezeichnung_englisch'] },
    { key: 'matchcode', label: 'Matchcode/Kurzname', aliases: ['kurzname', 'matchcode', 'suchbegriff'] },
    // Alte/fremde Artikelkennung. Bewusst viele Aliase: die Spalte heißt je nach
    // Export „alt_code", „Artikelnummer alt", „Referenz" oder „Fremdartikelnummer" —
    // alles dieselbe Sache (die Nummer, unter der der Artikel früher lief).
    // `normalizeColumnName` entfernt vorher Trenner/Diakritika, „Artikelnummer alt"
    // trifft also `artikelnummeralt`.
    {
      key: 'alt_code',
      label: 'Alt-/Fremd-Artikelnummer (auch „Referenz")',
      aliases: [
        'alt_code', 'alt_artikelnummer', 'fremdartikelnummer', 'altartikelnr',
        'artikelnummeralt', 'altenummer', 'altartikelnummer', 'referenz', 'reference', 'artikelreferenz',
      ],
    },
    { key: 'unit', label: 'Einheit', aliases: ['einheit', 'mengeneinheit', 'grundeinheit', 'me'] },
    { key: 'commodity_group', label: 'Warengruppe', aliases: ['warengruppe', 'warengr', 'artikelgruppe', 'wg'] },
    { key: 'sales_price_amount', label: 'VK-Preis', aliases: ['vk_preis', 'vk', 'verkaufspreis', 'vk_preis1'] },
    { key: 'sales_price_qty', label: 'VK Staffelmenge', aliases: ['vk_menge', 'staffelmenge1'] },
    { key: 'sales_price_2_amount', label: 'VK-Preis 2', aliases: ['vk_preis2'] },
    { key: 'sales_price_2_qty', label: 'VK-Preis 2 Menge', aliases: ['vk_menge2'] },
    { key: 'sales_price_3_amount', label: 'VK-Preis 3', aliases: ['vk_preis3'] },
    { key: 'sales_price_3_qty', label: 'VK-Preis 3 Menge', aliases: ['vk_menge3'] },
    { key: 'sales_price_4_amount', label: 'VK-Preis 4', aliases: ['vk_preis4'] },
    { key: 'sales_price_4_qty', label: 'VK-Preis 4 Menge', aliases: ['vk_menge4'] },
    { key: 'sales_price_5_amount', label: 'VK-Preis 5', aliases: ['vk_preis5'] },
    { key: 'sales_price_5_qty', label: 'VK-Preis 5 Menge', aliases: ['vk_menge5'] },
    { key: 'sales_price_6_amount', label: 'VK-Preis 6', aliases: ['vk_preis6'] },
    { key: 'sales_price_6_qty', label: 'VK-Preis 6 Menge', aliases: ['vk_menge6'] },
    { key: 'purchase_price_amount', label: 'EK-Preis', aliases: ['ek_preis', 'ek', 'einkaufspreis', 'ek_preis1'] },
    { key: 'avg_purchase_price_amount', label: 'Ø-EK-Preis (durchschn.)', aliases: ['durchschn_ek', 'durchschnittlicher_ek', 'avg_ek'] },
    { key: 'weight_kg', label: 'Gewicht (kg)', aliases: ['gewicht', 'gewicht_kg', 'weight'] },
    { key: 'stock_qty', label: 'Lagerbestand', aliases: ['lagerbestand', 'bestand', 'lager'] },
    { key: 'min_stock', label: 'Mindestbestand (Meldebestand)', aliases: ['meldebestand', 'mindestbestand', 'minbestand'] },
    { key: 'max_stock', label: 'Höchstbestand', aliases: ['hoechstbestand', 'maxbestand'] },
    // Die Quellspalte (BüroWARE) enthält die Lieferantennummer, keinen Namen —
    // Kunden UND Lieferanten teilen sich einen Adressraum (`kunden_lieferanten`/
    // `customers.number`), analog zu `purchase_orders.supplier_number`.
    { key: 'default_supplier_number', label: 'Standardlieferant (Lieferantennummer)', aliases: ['standardlieferant', 'lieferant', 'standardlief', 'standardlieferantennummer'] },
    { key: 'default_supplier_name', label: 'Standardlieferant (Name, falls keine Nummer vorhanden)', aliases: ['standardlieferant_name', 'lieferantenname'] },
    { key: 'last_sale_date', label: 'Letzter Verkauf (Datum)', aliases: ['datum_letzter_verkauf', 'letzter_verkauf'] },
    { key: 'long_text', label: 'Langtext (DE)', aliases: ['langtext', 'langtext_de', 'beschreibung'] },
    { key: 'long_text_en', label: 'Langtext (EN)', aliases: ['langtext_englisch', 'langtext_en'] },
    { key: 'extra_text', label: 'Zusatztext / interne Notiz', aliases: ['zusatztext', 'interne_notiz', 'bezeichnung_intern'] },
    { key: 'blocked_flag', label: 'Gesperrt (J/N)', aliases: ['gesperrt', 'blocked'] },
    { key: 'is_bom', label: 'Stückliste (J/N)', aliases: ['ist_stueckliste', 'stueckliste', 'bom'] },
    { key: 'inactive_flag', label: 'Inaktiv (J/N → aktiv)', aliases: ['inaktiv', 'inactive'] },
    { key: 'is_active', label: 'Aktiv (direkt)', aliases: ['aktiv', 'active'] },
  ],
  contacts: [
    { key: 'id', label: 'ID (uuid, leer = automatisch)' },
    { key: 'customer_number', label: 'Kunde (Kundennummer)', required: true, aliases: ['firma_nummer', 'kundennummer', 'kundennr', 'firma'] },
    { key: 'first_name', label: 'Vorname', aliases: ['vorname', 'firstname'] },
    { key: 'last_name', label: 'Nachname', aliases: ['nachname', 'lastname'] },
    { key: 'email', label: 'E-Mail', aliases: ['email', 'e_mail', 'mail'] },
    { key: 'phone', label: 'Telefon', aliases: ['telefon', 'tel', 'phone'] },
    { key: 'mobil', label: 'Mobil', aliases: ['mobil', 'mobile', 'handy'] },
    { key: 'funktion', label: 'Funktion', aliases: ['position', 'funktion', 'rolle'] },
    { key: 'department', label: 'Abteilung', aliases: ['abteilung', 'department'] },
    { key: 'is_primary', label: 'Hauptkontakt (1 = ja)', aliases: ['hauptkontakt', 'primary'] },
    { key: 'salutation', label: 'Anrede', aliases: ['anrede', 'salutation'] },
    { key: 'title', label: 'Titel', aliases: ['titel', 'title'] },
    { key: 'gender', label: 'Geschlecht', aliases: ['geschlecht', 'gender'] },
    { key: 'birth_date', label: 'Geburtsdatum', aliases: ['geburtsdatum', 'geburtstag'] },
    { key: 'language', label: 'Sprache', aliases: ['sprache', 'language'] },
    { key: 'fax', label: 'Telefax', aliases: ['telefax', 'fax'] },
    { key: 'phone_internal', label: 'Telefon (intern)', aliases: ['telefon_intern', 'tel_intern'] },
    { key: 'website', label: 'Website', aliases: ['web', 'website', 'homepage'] },
  ],
  crm_activities: [
    { key: 'customer_number', label: 'Kunde (Kundennummer)', required: true, aliases: ['nummer', 'kundennummer', 'kundennr', 'kunden_nr', 'firma_nummer', 'debitor'] },
    { key: 'activities', label: 'Aktivitäten-Verlauf (mehrzeilig, je Zeile ein Eintrag)', aliases: ['aktivitaeten', 'aktivitaet', 'historie', 'verlauf'] },
  ],
  delivery_addresses: [
    { key: 'id', label: 'ID (uuid, leer = automatisch)' },
    { key: 'number', label: 'Nummer', aliases: ['nummer', 'lieferadressnummer'] },
    { key: 'name', label: 'Name (komplett — nur falls Zeilen 1–3 leer)', aliases: ['name_komplett', 'name'] },
    { key: 'name1', label: 'Name (Zeile 1)', aliases: ['name1'] },
    { key: 'name2', label: 'Name (Zeile 2)', aliases: ['name2'] },
    { key: 'name3', label: 'Name (Zeile 3)', aliases: ['name3'] },
    { key: 'matchcode', label: 'Matchcode/Kurzname', aliases: ['matchcode'] },
    { key: 'street', label: 'Straße', aliases: ['strasse', 'str'] },
    { key: 'postal_code', label: 'PLZ', aliases: ['plz', 'postleitzahl'] },
    { key: 'city', label: 'Ort', aliases: ['ort', 'stadt'] },
    { key: 'country', label: 'Land (Code; führende Ziffern werden entfernt, z. B. „51IT" → IT)', aliases: ['land_code', 'laenderkennzeichen'] },
    { key: 'country_name', label: 'Land (Klartext)', aliases: ['land', 'landname'] },
    { key: 'email', label: 'E-Mail', aliases: ['email', 'mail'] },
    { key: 'phone', label: 'Telefon', aliases: ['telefon', 'tel'] },
    { key: 'notes', label: 'Notizen', aliases: ['notizen', 'notiz', 'bemerkung'] },
    { key: 'customer_number', label: 'Stammkunde (Kundennummer, bei L-Sätzen = „kurzname")', aliases: ['kurzname', 'kundennummer', 'kundennr', 'stammkunde'] },
  ],
  sales_documents: [
    { key: 'id', label: 'ID (uuid, leer = automatisch)' },
    { key: 'customer_number', label: 'Kunde (Kundennummer)', required: true, aliases: ['kundennummer', 'kundennr', 'kunde'] },
    { key: 'customer_name', label: 'Kundenname (z. B. name1)', aliases: ['name1', 'kundenname', 'name'] },
    { key: 'kind', label: 'Belegart (N/A/R/L/G → Angebot/Auftrag/Rechnung/Lieferschein/Gutschrift)', aliases: ['belegart'] },
    { key: 'number', label: 'Belegnummer', aliases: ['belegnummer', 'belegnr', 'nummer'] },
    { key: 'status', label: 'Status (DRAFT/SENT/… — SoftENGINE-Belegstatus passt nicht, leer lassen)', aliases: ['status'] },
    { key: 'currency', label: 'Währung (EUR/USD/CHF, sonst → EUR)', aliases: ['waehrung', 'currency'] },
    { key: 'tax_rate', label: 'Steuersatz % (leer = aus Netto/Steuer berechnet)', aliases: ['steuersatz', 'mwst_satz'] },
    { key: 'net_amount', label: 'Netto (EUR-Betrag)', aliases: ['netto', 'nettobetrag'] },
    { key: 'tax_amount', label: 'Steuer (EUR-Betrag)', aliases: ['steuer', 'steuerbetrag', 'mwst', 'ust'] },
    { key: 'gross_amount', label: 'Brutto (EUR-Betrag)', aliases: ['brutto', 'bruttobetrag'] },
    { key: 'document_date', label: 'Belegdatum', aliases: ['belegdatum', 'datum'] },
    { key: 'due_date', label: 'Fälligkeit (Rechnung)', aliases: ['faelligkeit', 'faelligkeitsdatum', 'zahlbar_bis'] },
    { key: 'delivery_date', label: 'Lieferdatum', aliases: ['lieferdatum', 'liefertermin'] },
    { key: 'project_code', label: 'Projektnummer (legt Projekt an & verknüpft)', aliases: ['projektnummer', 'projektnr', 'projekt'] },
    { key: 'project_name', label: 'Projektname (für neu angelegte Projekte)', aliases: ['projektname'] },
    { key: 'payment_terms_days', label: 'Zahlungsziel (Tage)', aliases: ['netto_tage', 'zahlungsziel'] },
    { key: 'cash_discount_percent', label: 'Skonto %', aliases: ['skonto_pct', 'skonto_prozent', 'skonto'] },
    { key: 'cash_discount_days', label: 'Skonto-Frist (Tage)', aliases: ['skonto_tage', 'skontofrist'] },
    { key: 'delivery_time_text', label: 'Lieferzeit (Text)', aliases: ['lieferzeit'] },
    { key: 'shipping_method', label: 'Versandart', aliases: ['versandart', 'versand'] },
    { key: 'shipping_weight_kg', label: 'Gewicht (kg)', aliases: ['gesamtgewicht_brutto', 'gewicht', 'gesamtgewicht'] },
    { key: 'reference_text', label: 'Bezugstext', aliases: ['bezugstext', 'referenz', 'betreff'] },
    { key: 'customer_street', label: 'Anschrift: Straße', aliases: ['strasse', 'str'] },
    { key: 'customer_zip', label: 'Anschrift: PLZ', aliases: ['plz', 'postleitzahl'] },
    { key: 'customer_city', label: 'Anschrift: Ort', aliases: ['ort', 'stadt'] },
  ],
  sales_document_items: [
    { key: 'id', label: 'ID (uuid, leer = automatisch)' },
    { key: 'document_kind', label: 'Belegart (N/A/R/L/G — Verknüpfung zum Beleg)', aliases: ['belegart'] },
    { key: 'document_number', label: 'Belegnummer (Verknüpfung zum Beleg)', aliases: ['belegnummer', 'belegnr'] },
    { key: 'position', label: 'Position / Reihenfolge', aliases: ['interne_lfdnr', 'lfdnr', 'positionsnummer', 'position', 'pos'] },
    { key: 'position_art', label: 'Positionsart (K=Überschrift, 0=Artikel, Z=Text, T=Gruppe, E=Ende)', aliases: ['positionsart', 'posart'] },
    { key: 'position_label', label: 'Positions-Nr. (z. B. 1.01 — steuert Reihenfolge & Text-Zuordnung)', aliases: ['positionsnummer'] },
    { key: 'article_number', label: 'Artikelnummer/-code', aliases: ['artikelcode', 'artikelnummer', 'artnr'] },
    { key: 'description', label: 'Bezeichnung', aliases: ['bezeichnung', 'text'] },
    { key: 'quantity', label: 'Menge', aliases: ['menge', 'anzahl'] },
    { key: 'unit', label: 'Einheit', aliases: ['mengeneinheit', 'einheit', 'me'] },
    { key: 'unit_price_amount', label: 'Einzelpreis (EUR-Betrag)', aliases: ['einzelpreis', 'ep', 'preis'] },
    { key: 'long_text', label: 'Langtext', aliases: ['langtext'] },
    { key: 'discount_percent', label: 'Rabatt %', aliases: ['rabatt_prozent', 'rabatt'] },
    { key: 'article_id', label: 'Artikel (uuid, optional)' },
  ],
  purchase_orders: [
    { key: 'id', label: 'ID (uuid, leer = automatisch)' },
    { key: 'supplier_number', label: 'Lieferant (Lieferantennummer)', required: true },
    { key: 'supplier_name', label: 'Lieferantenname' },
    { key: 'number', label: 'Bestellnummer' },
    { key: 'status', label: 'Status' },
    { key: 'currency', label: 'Währung' },
    { key: 'tax_rate', label: 'Steuersatz' },
    { key: 'net_amount', label: 'Netto (Mikro)' },
    { key: 'tax_amount', label: 'Steuer (Mikro)' },
    { key: 'gross_amount', label: 'Brutto (Mikro)' },
    { key: 'order_date', label: 'Bestelldatum' },
  ],
  purchase_order_items: [
    { key: 'id', label: 'ID (uuid)', required: true },
    { key: 'order_id', label: 'Bestellung (uuid)', required: true },
    { key: 'position', label: 'Position' },
    { key: 'article_id', label: 'Artikel (uuid)' },
    { key: 'article_number', label: 'Artikelnummer' },
    { key: 'description', label: 'Bezeichnung' },
    { key: 'long_text', label: 'Langtext' },
    { key: 'quantity', label: 'Menge' },
    { key: 'unit_price_amount', label: 'Einzelpreis (Mikro)' },
  ],
};

/**
 * Spaltennamen für den Vergleich normalisieren: Kleinschreibung, Diakritika
 * entfernt, nur Buchstaben/Ziffern. So matchen „EK-Preis", „ek_preis" und
 * „ekPreis" alle auf „ekpreis"; „Straße" und „strasse" auf „strasse".
 */
export function normalizeColumnName(name: string): string {
  return name
    .toLowerCase()
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '') // restliche Diakritika (combining marks)
    .replace(/[^a-z0-9]/g, '');
}

/**
 * Auto-Mapping: ordnet die Quell-Spalten (CSV-Kopf) den kanonischen Zielfeldern
 * zu — über den kanonischen Schlüssel und die hinterlegten Aliasse (dt./engl.).
 * Reihenfolge der Felder = Priorität; eine Quellspalte wird höchstens einmal
 * vergeben. Rückgabe: Zielfeld → Quellspalte (oder '' falls kein Treffer).
 */
export function autoMapColumns(entity: ImportEntity, sourceColumns: string[]): Record<string, string> {
  const cols = sourceColumns.map((raw) => ({ raw, norm: normalizeColumnName(raw) }));
  const used = new Set<string>();
  const result: Record<string, string> = {};
  for (const field of IMPORT_ENTITY_FIELDS[entity]) {
    const candidates = [field.key, ...(field.aliases ?? [])].map(normalizeColumnName);
    let match = '';
    for (const candidate of candidates) {
      const col = cols.find((c) => c.norm === candidate && !used.has(c.raw));
      if (col) {
        match = col.raw;
        break;
      }
    }
    if (match) used.add(match);
    result[field.key] = match;
  }
  return result;
}

/** Mapping: Entität → (kanonisches Zielfeld → Quell-NDJSON-Feld). */
export const importMappingSchema = z.record(z.string(), z.record(z.string(), z.string()));
export type ImportMapping = z.infer<typeof importMappingSchema>;

/**
 * Job-Ziel: eine Per-Tabelle-Entität (CSV) ODER ein gebündelter NDJSON-Import.
 * `sales_bundle` = Verkaufsbelege mit verschachtelten Positionen (Kopf+Positionen
 * in einem NDJSON-Satz) → wird serverseitig in Belege + Belegpositionen entpackt.
 */
export const IMPORT_BUNDLE_ENTITIES = ['sales_bundle', 'purchase_bundle'] as const;
export type ImportBundleEntity = (typeof IMPORT_BUNDLE_ENTITIES)[number];
const JOB_ENTITY_VALUES = [...IMPORT_ENTITIES, ...IMPORT_BUNDLE_ENTITIES] as const;

/** Job anlegen: Name/Größe + optionale Ziel-Tabelle (Per-Tabelle-CSV/NDJSON-Bündel) + Mapping. */
export const createImportJobSchema = z.object({
  sourceName: z.string().min(1),
  sizeBytes: z.number().int().nonnegative().optional(),
  /** Ziel: Per-Tabelle-Entität, `sales_bundle` (NDJSON-Belege), oder weglassen = Manifest-Bündel. */
  entity: z.enum(JOB_ENTITY_VALUES).optional(),
  mapping: importMappingSchema.optional(),
  /** Zeilenfilter: nur Zeilen importieren, deren (Quell-)Spalte `filterColumn` == `filterValue`. */
  filterColumn: z.string().optional(),
  filterValue: z.string().optional(),
});
export type CreateImportJobInput = z.infer<typeof createImportJobSchema>;

export const importJobResponseSchema = z.object({
  id: z.string(),
  status: importJobStatusSchema,
  sourceName: z.string(),
  sizeBytes: z.number().nullable(),
  targetLabel: z.string().nullable(),
  report: importReportSchema.nullable(),
  error: z.string().nullable(),
  startedAt: z.string().datetime().nullable(),
  finishedAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
});
export type ImportJobResponse = z.infer<typeof importJobResponseSchema>;
